import { useEffect, useState } from 'react'
import {
  Building2,
  Save,
  AlertCircle,
  CheckCircle2,
  Loader2,
  MapPin,
  Mail,
  Phone,
  Globe,
  Hash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { getApiErrorKey } from '../../utils/apiError'

// ── ERP admin profile (company info) ─────────────────────────────────────────

interface ErpProfileData {
  erp_name: string
  erp_description: string
  siren: string
  website: string
  address: string
  postal_code: string
  city: string
  country_code: string
  contact_email: string
  contact_phone: string
}

const EMPTY_ERP_PROFILE: ErpProfileData = {
  erp_name: '',
  erp_description: '',
  siren: '',
  website: '',
  address: '',
  postal_code: '',
  city: '',
  country_code: 'FR',
  contact_email: '',
  contact_phone: '',
}

function ErpAdminProfile() {
  const { t } = useTranslation()
  const [form, setForm] = useState<ErpProfileData>(EMPTY_ERP_PROFILE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .getAdminProfile()
      .then((d) => {
        setForm({
          erp_name: d.erp_name ?? '',
          erp_description: d.erp_description ?? '',
          siren: d.siren ?? '',
          website: d.website ?? '',
          address: d.address ?? '',
          postal_code: d.postal_code ?? '',
          city: d.city ?? '',
          country_code: d.country_code ?? 'FR',
          contact_email: d.contact_email ?? '',
          contact_phone: d.contact_phone ?? '',
        })
      })
      .catch(() => setError(t('adminProfile.loadError', 'Impossible de charger le profil.')))
      .finally(() => setLoading(false))
  }, [t])

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    setSuccess(false)
    setError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSuccess(false)
    setError(null)
    try {
      await api.updateAdminProfile(form)
      setSuccess(true)
    } catch (err: unknown) {
      setError(getApiErrorKey(err, 'adminProfile.saveError'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('adminProfile.title', 'Fiche fournisseur')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('adminProfile.subtitle', 'Informations de votre société')}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {t(error)}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {t('adminProfile.saveSuccess', 'Profil sauvegardé.')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company info */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('adminProfile.sectionCompany', 'Société')}
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminProfile.erpName', 'Nom de la société')} *
            </label>
            <input
              type="text"
              name="erp_name"
              value={form.erp_name}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminProfile.description', 'Description')}
            </label>
            <textarea
              name="erp_description"
              value={form.erp_description}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Hash className="w-3 h-3" />
                {t('adminProfile.siren', 'SIREN')}
              </label>
              <input
                type="text"
                name="siren"
                value={form.siren}
                onChange={handleChange}
                maxLength={14}
                pattern="\d{9,14}"
                placeholder="123456789"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {t('adminProfile.website', 'Site web')}
              </label>
              <input
                type="url"
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder="https://www.example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </section>

        {/* Address */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {t('adminProfile.sectionAddress', 'Adresse postale')}
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminProfile.address', 'Adresse')}
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              maxLength={255}
              placeholder="1 rue de la Paix"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminProfile.postalCode', 'Code postal')}
              </label>
              <input
                type="text"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                maxLength={20}
                placeholder="75001"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminProfile.city', 'Ville')}
              </label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                maxLength={100}
                placeholder="Paris"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminProfile.countryCode', 'Pays (code)')}
              </label>
              <input
                type="text"
                name="country_code"
                value={form.country_code}
                onChange={handleChange}
                maxLength={2}
                placeholder="FR"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
              />
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
            <Mail className="w-4 h-4" />
            {t('adminProfile.sectionContact', 'Contact')}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {t('adminProfile.contactEmail', 'Email de contact')}
              </label>
              <input
                type="email"
                name="contact_email"
                value={form.contact_email}
                onChange={handleChange}
                maxLength={255}
                placeholder="contact@example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {t('adminProfile.contactPhone', 'Téléphone')}
              </label>
              <input
                type="tel"
                name="contact_phone"
                value={form.contact_phone}
                onChange={handleChange}
                maxLength={30}
                placeholder="+33 1 23 45 67 89"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </section>

        {/* Save button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving
              ? t('adminProfile.saving', 'Sauvegarde…')
              : t('adminProfile.save', 'Enregistrer')}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function AdminProfile() {
  return <ErpAdminProfile />
}
